import React, { useContext, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    IconButton,
    Menu,
    MenuItem,
    Checkbox,
    TableSortLabel,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Toolbar,
    Typography,
    Box,
    useMediaQuery,
    CircularProgress,
    Chip,
} from '@mui/material';
import {
    MoreVert as MoreVertIcon,
    Delete as DeleteIcon,
    Settings,
    Visibility,
    Download
} from '@mui/icons-material';
import moment from 'moment';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import formatMoney from '../../utils/formatMoney';
import axios from 'axios';
import { AuthContext } from '../../utils/AuthContext';
import { downloadCommissionStatement } from '../../services/ClientsService';

const columns = [
    { id: 'agentId', label: 'AgentID', minWidth: 90 },
    { id: 'agentName', label: 'Agent Name', minWidth: 120 },
    { id: 'commissionDate', label: 'Commission Date', minWidth: 150 },
    { id: 'csNo', label: 'CS No', minWidth: 100 },
    { id: 'type', label: 'Type', minWidth: 90 },
    { id: 'clientName', label: 'Client Name', minWidth: 150 },
    { id: 'referral', label: 'Referral', minWidth: 120 },
    { id: 'insurer', label: 'Insurer', minWidth: 120 },
    { id: 'totalFee', label: 'Total Fee', minWidth: 130, align: 'right' },
    { id: 'caseOfficer', label: 'Case Officer', minWidth: 120, align: 'center' },
    { id: 'status', label: 'Status', minWidth: 120, align: 'center' },
    { id: 'actions', label: 'Actions', width: 80, align: 'center' },
];

export default function ClientsTable({
    clients,
    page,
    rowsPerPage,
    totalRecords,
    onPageChange,
    onRowsPerPageChange,
    onDelete,
    isLoading,
    setShowClientInfoDialog,
    setSelectedClient }) {

    const isMobile = useMediaQuery('(max-width:600px)');
    const [visibleColumns, setVisibleColumns] = useState(getInitialVisibleColumns());
    const [openColumnDialog, setOpenColumnDialog] = useState(false);
    const [selected, setSelected] = useState([]);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('');
    const [expandedCells, setExpandedCells] = useState({});

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRowId, setMenuRowId] = useState(null);

    const { auth } = useContext(AuthContext);


    const toggleColumnVisibility = (columnId) => {
        setVisibleColumns((prev) => {
            // prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId]
            let columns = [];
            if (prev.includes(columnId)) {
                columns = prev.filter((id) => id !== columnId);
            } else {
                columns = [...prev, columnId]
            }

            localStorage.setItem('agent_connect_clients_visible_columns', JSON.stringify(columns));

            return columns;
        }
        );
    };

    function getInitialVisibleColumns() {
        const visibleColumnsStr = localStorage.getItem('agent_connect_clients_visible_columns');

        if (!visibleColumnsStr) {
            return columns.map((col) => col.id);
        }

        return JSON.parse(visibleColumnsStr);
    }

    const handleOpenColumnDialog = () => {
        setOpenColumnDialog(true);
    };

    const handleCloseColumnDialog = () => {
        setOpenColumnDialog(false);
    };

    const isSelected = (id) => selected.includes(id);

    const isCellExpanded = (rowId, columnId) => {
        return expandedCells[`${rowId}-${columnId}`] || false;
    };

    const toggleShowFullText = (rowId, columnId) => {
        setExpandedCells((prev) => ({
            ...prev,
            [`${rowId}-${columnId}`]: !prev[`${rowId}-${columnId}`],
        }));
    };

    const renderTruncatedContent = (content, rowId, columnId) => {
        const charLimit = 30;

        if (!content) return content;

        if (content.length <= charLimit) return content;

        const isExpanded = isCellExpanded(rowId, columnId);

        return (
            <>
                {isExpanded ? content : `${content.slice(0, charLimit)}...`}{" "}
                <span
                    onClick={() => toggleShowFullText(rowId, columnId)}
                    style={{ color: "blue", cursor: "pointer" }}
                >
                    {isExpanded ? "Show less" : "Show more"}
                </span>
            </>
        );
    };

    const formatReferredIndividual = (row) => {
        const parsedData = JSON.parse(row.referred_individual);
        const individuals = parsedData.map((item) => item.individual);
        if (individuals.length > 1) {
            const formattedIndividuals = individuals.join(", ");
            return formattedIndividuals;
        } else {
            return individuals[0];
        }
    };

    const formatReferredGroup = (row) => {
        const parsedData = JSON.parse(row.referred_group);
        const groups = parsedData.map((item) => item.group);
        if (groups.length > 1) {
            const formattedGroups = groups.join(", ");
            return formattedGroups;
        } else {
            return groups[0];
        }
    };

    const getFirstReferredIndividual = (row) => {
        const parsedData = JSON.parse(row.referred_individual);
        const individuals = parsedData.map((item) => item.individual);
        if (individuals.length > 0) {
            if (row.referred_individual_count > 1) {
                return individuals[0] + " and others";
            } else {
                return individuals[0];
            }

        }
    };

    const getFirstReferredGroup = (row) => {
        const parsedData = JSON.parse(row.referred_group);
        const groups = parsedData.map((item) => item.group);
        if (groups.length > 0) {
            if (row.referred_group_count > 1) {
                return groups[0] + " and others";
            } else {
                return groups[0];
            }
        }
    };

    const mappedClients = clients.map((client, index) => ({
        id: client.id || index + 1,
        agentId: client.agent.agent_id || 'N/A',
        agentName: client.agent.first_name + ' ' + client.agent.last_name,
        commissionDate: client.date || 'Not available',
        csNo: client.cs_no || 'Not provided',
        clientName: getFirstReferredIndividual(client) || getFirstReferredGroup(client) || '',
        type: client.commission_type || 'Unknown',
        referral: client.membership_type || 'No referral',
        insurer: client.insurer || 'Unknown insurer',
        totalFee: client.currency + ' ' + formatMoney(client.cat) || 0,
        status: client.status || 'Pending',
        caseOfficer: client.case_officer || 'Not assigned',
    }));

    const handleDeleteDialogOpen = () => {
        setOpenDeleteDialog(true);
    };

    const handleDeleteDialogClose = () => {
        setOpenDeleteDialog(false);
    };

    const handleDelete = () => {
        onDelete(selected);
        setSelected([]);
        setOpenDeleteDialog(false);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    function getStatusBackgroundColor(status) {
        if (status === 'Cancelled') {
            return '#FBE7E8';
        } else if (status !== 'Paid') {
            // Pending
            return '#FEF2E5';
        } else if (status === 'Paid') {
            return '#EBF9F1';
        }

        return null;
    }

    function getStatusColor(status) {
        if (status === 'Cancelled') {
            return '#A30D11';
        } else if (status !== 'Paid') {
            // Pending
            return '#CD6200';
        } else if (status === 'Paid') {
            return '#1F9254';
        }

        return null;
    }

    const sortedClients = [...mappedClients].sort((a, b) => {
        if (orderBy) {
            const isAsc = order === 'asc';
            if (typeof a[orderBy] === 'string') {
                return isAsc
                    ? a[orderBy].localeCompare(b[orderBy])
                    : b[orderBy].localeCompare(a[orderBy]);
            }
            return isAsc ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy];
        }
        return 0;
    });

    const handleMenuOpen = (event, rowId) => {
        setMenuAnchorEl(event.currentTarget);
        setMenuRowId(rowId);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuRowId(null);
    };

    const downloadStatement = async (id) => {
        try {
            const blob = await downloadCommissionStatement({ token: auth.token, id });
            const url = window.URL.createObjectURL(new Blob([blob]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `Commission_Statement_${id}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (error) {
            console.error('Error downloading commission statement:', error);
        }
    };

    const renderCellContent = (column, row) => {

        function findClientById(id) {
            for (let i = 0; i < clients.length; i++) {
                if (clients[i].id === id) {
                    console.log(clients[i]);
                    return clients[i];
                }
            }
        }

        if (column.id === 'actions') {
            return (
                <>
                    <IconButton onClick={(event) => handleMenuOpen(event, row.id)}>
                        <MoreVertIcon />
                    </IconButton>
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={menuRowId === row.id}
                        onClose={handleMenuClose}
                    >
                        <MenuItem onClick={() => {
                            setSelectedClient(findClientById(row.id));
                            setShowClientInfoDialog(true);
                            handleMenuClose();
                        }}>
                            <Visibility fontSize="small" sx={{ mr: 1 }} /> View
                        </MenuItem>
                        {row.status === 'Paid' && (
                            <MenuItem onClick={() => {
                                downloadStatement(row.id);
                                handleMenuClose();
                            }}>
                                <Download fontSize="small" sx={{ mr: 1 }} /> Download Statement
                            </MenuItem>
                        )}
                    </Menu>
                </>
            );
        }
        else if (column.id === 'commissionDate') {
            return moment(row[column.id], "YYYY-MM-DD").format("MM/DD/YYYY");
        } else if (column.id === 'clientName') {
            return renderTruncatedContent(row[column.id], row.id, column.id);
        } else if (column.id === 'status') {
            const status = row[column.id];
            let shortStatus = '';

            if (status === 'Paid') {
                shortStatus = 'Paid';
            } else if (status === 'Cancelled') {
                shortStatus = 'Cancelled';
            } else {
                // pending
                shortStatus = 'Pending';
            }

            return <Chip
                label={shortStatus}
                sx={{
                    backgroundColor: getStatusBackgroundColor(shortStatus),
                    color: getStatusColor(shortStatus)
                }}
            />
        } else {
            return row[column.id];
        }
    };

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', flexGrow: 1, overflowY: 'auto' }}>
            {/* Toolbar */}
            {/* <Toolbar>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleDeleteDialogOpen}
                        sx={{
                            // visibility: selected.length > 0 ? 'visible' : 'hidden', // Toggle visibility based on selected.length
                            mr: 2, // Space between button and table
                            visibility: 'hidden',
                        }}
                    >
                        Delete {selected.length} {selected.length > 1 ? 'Items' : 'Item'}
                    </Button>
                    <Button
                        startIcon={<Settings />}
                        onClick={handleOpenColumnDialog}
                        variant="contained"
                    >
                        Column Settings
                    </Button>
                </Box>
            </Toolbar> */}

            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'end' }}>
                <Button
                    startIcon={<ViewWeekOutlinedIcon />}
                    onClick={handleOpenColumnDialog}
                    variant="text"
                >
                    Column Settings
                </Button>
            </Box>

            {/* Table */}
            <TableContainer variant='outlined' component={Paper} sx={{ flexGrow: 1, overflowY: 'auto', width: '99.50%' }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                {/* <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selected.length > 0 && selected.length === mappedClients.length}
                                        onChange={(event) =>
                                            setSelected(event.target.checked ? mappedClients.map((row) => row.id) : [])
                                        }
                                    />
                                </TableCell> */}
                                {columns
                                    .filter((col) => visibleColumns.includes(col.id))
                                    .map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.align}
                                            style={{ minWidth: column.minWidth, fontWeight: 600, color: '#424242' }}
                                        >
                                            <TableSortLabel
                                                active={orderBy === column.id}
                                                direction={orderBy === column.id ? order : 'asc'}
                                                onClick={() => handleRequestSort(column.id)}
                                            >
                                                {column.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                sortedClients.length === 0 &&
                                <TableRow>
                                    <TableCell colSpan={visibleColumns.length + 1} sx={{ textAlign: 'center', height: 40 }}>No Clients</TableCell>
                                </TableRow>

                            }
                            {sortedClients.map((row) => (
                                <TableRow key={row.id}>
                                    {/* <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={isSelected(row.id)}
                                            onChange={() =>
                                                setSelected((prev) =>
                                                    isSelected(row.id)
                                                        ? prev.filter((id) => id !== row.id)
                                                        : [...prev, row.id]
                                                )
                                            }
                                        />
                                    </TableCell> */}
                                    {columns
                                        .filter((col) => visibleColumns.includes(col.id))
                                        .map((column) => (
                                            <TableCell key={column.id} align={column.align} sx={{ color: '#000' }}>
                                                {renderCellContent(column, row)}
                                            </TableCell>
                                        ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
            <TablePagination
                className="table-pagination"
                sx={{ overflowY: 'hidden', minHeight: '45px', mb: 4 }}
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
            />

            {/* Column Visibility Dialog */}
            <Dialog open={openColumnDialog} onClose={handleCloseColumnDialog}>
                <DialogTitle>Column Settings</DialogTitle>
                <DialogContent>
                    {columns.map((column) => (
                        <FormControlLabel
                            key={column.id}
                            control={
                                <Checkbox
                                    checked={visibleColumns.includes(column.id)}
                                    onChange={() => toggleColumnVisibility(column.id)}
                                />
                            }
                            label={column.label}
                        />
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseColumnDialog}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
